import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { validateAccessCode, createSession } from '../utils/auth';
import { useMarket } from '../context/MarketContext';
import { useAuth } from '../context/AuthContext';
import { useMarketNavigate } from '../hooks/useMarketNavigate';
import SEOHead from '../components/SEOHead';
import './Login.css';

function Login() {
  // Shared-code auth (TCC)
  const [accessCode, setAccessCode] = useState('');

  // Email/password auth (OTC / new RBAC)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useMarketNavigate();
  const location = useLocation();
  const market = useMarket();
  const { login, isAuthenticated } = useAuth();

  // Determine which auth mode to use based on market feature flag
  const useNewAuth = market.features?.venueOwnerAccounts === true;

  // Get the intended destination (for post-login redirect)
  const from = location.state?.from?.pathname || null;

  // SEOHead handles the title now — see JSX below

  // Redirect if already authenticated (new auth)
  useEffect(() => {
    if (useNewAuth && isAuthenticated) {
      navigate(from || '/', { replace: true });
    }
  }, [isAuthenticated, navigate, from, useNewAuth]);

  // --- Legacy shared-code login (TCC) ---
  const handleLegacySubmit = (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = validateAccessCode(accessCode);

    if (result.valid) {
      createSession(result.role);
      if (result.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } else {
      setError(result.error);
      setIsLoading(false);
    }
  };

  // --- New email/password login (OTC / RBAC) ---
  const handleNewAuthSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!email.trim() || !password) {
      setError('Please enter your email and password.');
      setIsLoading(false);
      return;
    }

    const result = await login(email.trim(), password, market.id);

    if (result.success) {
      // Check if password must be changed
      if (result.passwordMustChange) {
        navigate('/change-password', { replace: true });
        return;
      }

      // Redirect based on role
      if (from) {
        navigate(from, { replace: true });
      } else {
        const role = result.user.role;
        if (role === 'super_admin') {
          navigate('/admin', { replace: true });
        } else if (role === 'admin') {
          navigate('/admin', { replace: true });
        } else if (role === 'venue_owner') {
          navigate('/admin/venue', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }
    } else {
      setError(result.error);
      setIsLoading(false);
    }
  };

  // Apply market theme colors as CSS variables
  const loginPageStyle = {
    '--market-primary': market.theme.primaryColor,
    '--market-accent': market.theme.accentColor,
  };

  return (
    <div className="login-page" style={loginPageStyle}>
      <SEOHead title="Sign In" noindex market={market} />
      <div className="login-container">
        <header className="login-header">
          <img src={market.logo} alt={market.logoAlt} className="login-logo" />
          <p className="login-subtitle">{market.tagline}</p>
        </header>

        {useNewAuth ? (
          /* --- New RBAC Login Form (email/password) --- */
          <form className="login-form" onSubmit={handleNewAuthSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                autoFocus
                autoComplete="email"
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <button type="submit" className="login-button" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>

            <p className="login-note">Admin and venue owner access only.</p>
          </form>
        ) : (
          /* --- Legacy Login Form (access code) --- */
          <form className="login-form" onSubmit={handleLegacySubmit}>
            <div className="form-group">
              <label htmlFor="access-code">Access Code</label>
              <input
                type="text"
                id="access-code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder="Enter your access code"
                autoFocus
                disabled={isLoading}
              />
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <button type="submit" className="login-button" disabled={isLoading}>
              {isLoading ? 'Entering...' : 'Enter'}
            </button>

            <p className="login-note">Access provided to subscribers.</p>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login;
